import { useState } from "react";
import { api } from "@/context/AuthContext";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Plus, Minus, ArrowDownToLine, ArrowUpFromLine, Loader2,
} from "lucide-react";

/**
 * Quick-Adjust popover for stock changes (Wareneingang / Lagerabgang).
 * Renders two buttons (+ / -) inline; clicking opens a popover with amount + reason.
 */
const StockAdjustPopover = ({ material, onAdjusted }) => {
  const [open, setOpen] = useState(false);
  const [direction, setDirection] = useState("in"); // "in" = Wareneingang, "out" = Abgang
  const [amount, setAmount] = useState("1");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const openWith = (dir) => {
    setDirection(dir);
    setAmount("1");
    setReason("");
    setOpen(true);
  };

  const submit = async () => {
    const num = Math.abs(parseFloat(amount));
    if (!num || isNaN(num)) {
      toast.error("Bitte eine gültige Menge eingeben");
      return;
    }
    const delta = direction === "in" ? num : -num;
    setSubmitting(true);
    try {
      const res = await api.post(`/api/materials/${material.id}/adjust-stock`, { amount: delta, reason });
      toast.success(
        direction === "in"
          ? `+${num} ${material.einheit} eingebucht (neuer Bestand: ${res.data.new_bestand})`
          : `-${num} ${material.einheit} ausgebucht (neuer Bestand: ${res.data.new_bestand})`
      );
      if (res.data.warning) toast.warning(res.data.warning);
      setOpen(false);
      onAdjusted?.(res.data.new_bestand);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Buchen");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <span className="inline-flex gap-1">
          <Button
            size="icon"
            variant="ghost"
            className="h-7 w-7 hover:bg-emerald-100 hover:text-emerald-700"
            onClick={(e) => { e.stopPropagation(); openWith("in"); }}
            title="Wareneingang"
            data-testid={`stock-in-${material.id}`}
          >
            <Plus className="w-3.5 h-3.5" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="h-7 w-7 hover:bg-red-100 hover:text-red-700"
            onClick={(e) => { e.stopPropagation(); openWith("out"); }}
            title="Lagerabgang"
            data-testid={`stock-out-${material.id}`}
          >
            <Minus className="w-3.5 h-3.5" />
          </Button>
        </span>
      </PopoverTrigger>
      <PopoverContent className="w-80" align="end" onClick={(e) => e.stopPropagation()}>
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            {direction === "in" ? (
              <ArrowDownToLine className="w-4 h-4 text-emerald-600" />
            ) : (
              <ArrowUpFromLine className="w-4 h-4 text-red-600" />
            )}
            <p className="font-medium text-sm">
              {direction === "in" ? "Wareneingang" : "Lagerabgang"}
            </p>
          </div>
          <p className="text-xs text-muted-foreground">
            {material.bezeichnung} · Aktueller Bestand: <span className="font-medium text-foreground">{material.bestand} {material.einheit}</span>
          </p>

          <div className="flex gap-2 p-1 bg-muted rounded-md">
            <button
              type="button"
              onClick={() => setDirection("in")}
              className={`flex-1 py-1.5 rounded text-xs font-medium transition-colors ${direction === "in" ? "bg-background shadow-sm" : "text-muted-foreground"}`}
              data-testid="dir-in"
            >
              + Eingang
            </button>
            <button
              type="button"
              onClick={() => setDirection("out")}
              className={`flex-1 py-1.5 rounded text-xs font-medium transition-colors ${direction === "out" ? "bg-background shadow-sm" : "text-muted-foreground"}`}
              data-testid="dir-out"
            >
              − Ausgang
            </button>
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Menge ({material.einheit})</Label>
            <Input
              type="number"
              step="any"
              min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              autoFocus
              data-testid="adjust-amount-input"
            />
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Notiz (optional)</Label>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={direction === "in" ? "z.B. Lieferung Stahlgruber" : "z.B. Bestandskorrektur"}
              data-testid="adjust-reason-input"
            />
          </div>

          <div className="bg-muted/50 rounded-md p-2 text-xs flex justify-between">
            <span className="text-muted-foreground">Neuer Bestand:</span>
            <span className="font-semibold">
              {(parseFloat(material.bestand || 0) + (direction === "in" ? 1 : -1) * (parseFloat(amount) || 0)).toFixed(2)} {material.einheit}
            </span>
          </div>

          <div className="flex gap-2 justify-end pt-1">
            <Button variant="outline" size="sm" onClick={() => setOpen(false)}>Abbrechen</Button>
            <Button
              size="sm"
              onClick={submit}
              disabled={submitting}
              className={direction === "in" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700"}
              data-testid="adjust-submit"
            >
              {submitting && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
              {direction === "in" ? "Einbuchen" : "Ausbuchen"}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default StockAdjustPopover;
